import { useEffect, useState } from 'react';

import { SidebarMenuItem, BadgeCircle } from '@backrs_ui/common-ui';

import { ReactComponent as HomeIcon } from '@backrs_ui/grow-ui/public/images/home-icon.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LAYOUT_SCROLL_ID } from '@backrs_ui/grow-ui/src/components/PageShell/pageShell.layout';
import { isUnsupportedEnrollmentType } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { institutionLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import { getDemoAwareRoute } from '@backrs_ui/grow-ui/src/utils/demoUtils';
import { getSessionRoute } from '@backrs_ui/grow-ui/src/utils/getSessionRoute';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

import { Box, Tooltip } from '@mui/material';

import { SectionDependencies } from './menuSections.hook';

const SIDEBAR_TITLES = appStrings.modules.backrsPageShell.menuItems.sidebar;
const REOPEN_TOOLTIP = appStrings.modules.pageShell.lubavPanel.reopenTooltip;

const BACKRS_APP_URL = process.env.REACT_APP_BACKRS_URL;

const REOPEN_HINT_DURATION_MS = 4000;

const HomeIconWithBadge = () => {
  const [hintOpen, setHintOpen] = useState(true);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setHintOpen(false), REOPEN_HINT_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <Tooltip
      title={REOPEN_TOOLTIP}
      placement="right"
      arrow
      open={hintOpen || hovered}
      PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, 20] } }] }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HomeIcon />
        <Box
          sx={{
            position: 'absolute',
            top: '58%',
            left: '48%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <BadgeCircle innerOnly loading autoStopMs={3000} staticDotDiameter="6px" />
        </Box>
      </Box>
    </Tooltip>
  );
};

export const SidebarItems = ({
  navigate,
  location,
  doesUserHaveBlueprint,
  guestHasBlueprint,
  isUnauthenticated,
  isGuest,
  userPermissions,
  academicEnrollmentTypes,
  lubavSessions,
  hasMoreSessions,
  onLoadMoreSessions,
  isMobile,
  isCoachingEnabled,
  isPaywalled,
  isPanelCollapsed,
  onExpandPanel,
  isInstitutionAdmin,
  onOpenGettingStarted,
  onNewChat,
  heldRoots,
  operatorInstitutionId,
  onOperatorInstitutionSelect,
}: SectionDependencies): SidebarMenuItem[] => {
  if (isPaywalled) return [];

  const isLoggedOut = isUnauthenticated && !isGuest;

  const hasBlueprint = isGuest ? guestHasBlueprint : doesUserHaveBlueprint;
  const blueprintPath = getDemoAwareRoute(
    hasBlueprint ? APP_ROUTE.MY_BLUEPRINT : APP_ROUTE.SURVEY,
    isGuest,
  );
  const isBlueprintActive = [
    APP_ROUTE.MY_BLUEPRINT,
    APP_ROUTE.GENERATING_BLUEPRINT,
    APP_ROUTE.SURVEY,
    APP_ROUTE.DEMO_MY_BLUEPRINT,
    APP_ROUTE.DEMO_GENERATING_BLUEPRINT,
    APP_ROUTE.DEMO_SURVEY,
  ].some((route) => route === location.pathname);

  const lubavMenuItem: SidebarMenuItem = {
    id: 'sidemenu-home',
    title: SIDEBAR_TITLES.lubav,
    hidden: isLoggedOut || isMobile,
    leftIcon: isPanelCollapsed ? { component: <HomeIconWithBadge /> } : { iconName: 'HomeLine' },
    variant: {
      type: 'EXPANDABLE',
      suppressExpandOnClick: isPanelCollapsed,
      onClick: () => {
        if (isPanelCollapsed) {
          onExpandPanel();

          return;
        }

        if (location.pathname === APP_ROUTE.HOME) {
          return;
        }

        navigate(APP_ROUTE.HOME);

        setTimeout(() => {
          const pageContainer = document.getElementById(LAYOUT_SCROLL_ID);

          if (pageContainer && location.pathname !== APP_ROUTE.HOME) {
            pageContainer.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 1);
      },
      subItems: [
        {
          id: 'sidemenu-new-chat',
          title: SIDEBAR_TITLES.newChat,
          leftIcon: { iconName: 'PlusSquare', diameter: '16px' },
          variant: {
            type: 'NAVIGATION_ITEM' as const,
            itemVariant: 'quaternary' as const,
            isActive:
              location.pathname === APP_ROUTE.HOME &&
              !new URLSearchParams(location.search).get('session_id'),
            onClick: onNewChat,
          },
        },
        ...lubavSessions.map((session) => ({
          title: session.session_title,
          leftIcon: { component: <BadgeCircle />, onlyShowOnActive: true },
          variant: {
            type: 'NAVIGATION_ITEM' as const,
            itemVariant: 'quaternary' as const,
            isActive: new URLSearchParams(location.search).get('session_id') === String(session.id),
            onClick: () => {
              const route = getSessionRoute(session);

              navigate(route);
            },
          },
        })),
      ],
      scrollable: {
        maxVisibleItems: 5,
        hasMore: hasMoreSessions,
        onLoadMore: onLoadMoreSessions,
      },
    },
  };

  const coachingDashboardMenuItem: SidebarMenuItem = {
    id: 'sidemenu-coaching-dashboard',
    title: isInstitutionAdmin ? SIDEBAR_TITLES.adminDashboard : SIDEBAR_TITLES.coachingDashboard,
    hidden: !userPermissions?.access_coach_dashboard.allowed,
    leftIcon: { iconName: isInstitutionAdmin ? 'Users01' : 'BarChartSquare02' },
    variant: {
      isActive: location.pathname === APP_ROUTE.COACHING_DASHBOARD,
      type: 'NAVIGATION_ITEM',
      onClick: () => navigate(APP_ROUTE.COACHING_DASHBOARD),
    },
  };

  const rostersAdminMenuItem: SidebarMenuItem = {
    id: 'sidemenu-rosters-admin',
    title: SIDEBAR_TITLES.rostersAdmin,
    leftIcon: { iconName: 'BarChartSquare02' },
    variant: {
      isActive: location.pathname === APP_ROUTE.ROSTERS_ADMIN,
      type: 'NAVIGATION_ITEM',
      onClick: () => navigate(APP_ROUTE.ROSTERS_ADMIN),
    },
  };

  const coachingAnalyticsMenuItem: SidebarMenuItem = {
    id: 'sidemenu-coaching-analytics',
    title: SIDEBAR_TITLES.coachingAnalytics,
    hidden: !userPermissions?.access_cohort_analytics.allowed,
    leftIcon: { iconName: 'PieChart02' },
    variant: {
      isActive: location.pathname === APP_ROUTE.COACHING_ANALYTICS,
      type: 'NAVIGATION_ITEM',
      onClick: () => navigate(APP_ROUTE.COACHING_ANALYTICS),
    },
  };

  return [
    lubavMenuItem,
    ...(isInstitutionAdmin
      ? [coachingDashboardMenuItem, coachingAnalyticsMenuItem, rostersAdminMenuItem]
      : []),
    {
      id: 'sidemenu-blueprint',
      title: SIDEBAR_TITLES.blueprint,
      hidden: isLoggedOut || isMobile || isInstitutionAdmin,
      leftIcon: { iconName: 'Map01' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: isBlueprintActive,
        onClick: () => navigate(blueprintPath),
      },
    },
    {
      id: 'sidemenu-assess-my-track',
      title: SIDEBAR_TITLES.assessMyTrack,
      hidden:
        isLoggedOut ||
        (isInstitutionAdmin && !isMobile) ||
        (!!academicEnrollmentTypes && isUnsupportedEnrollmentType(academicEnrollmentTypes)),
      leftIcon: { iconName: 'Route' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname === APP_ROUTE.ASSESS_MY_TRACK,
        onClick: () => navigate(APP_ROUTE.ASSESS_MY_TRACK),
      },
    },
    {
      id: 'sidemenu-tools',
      title: SIDEBAR_TITLES.tools,
      hidden: isLoggedOut || isMobile || isInstitutionAdmin,
      leftIcon: { iconName: 'PuzzlePiece01' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname === APP_ROUTE.TOOLS,
        onClick: () => navigate(APP_ROUTE.TOOLS),
      },
    },
    {
      id: 'sidemenu-coaching',
      title: SIDEBAR_TITLES.coaching,
      hidden:
        !isCoachingEnabled ||
        !userPermissions?.access_deep_dives.allowed ||
        isMobile ||
        isInstitutionAdmin,
      leftIcon: { iconName: 'StickerCircle' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname.startsWith(APP_ROUTE.DEEP_DIVES),
        onClick: () => navigate(APP_ROUTE.DEEP_DIVES),
      },
    },
    // MOBILE ONLY SIDEBAR ITEMS - START
    {
      id: 'sidemenu-priorities',
      title: SIDEBAR_TITLES.priorities,
      hidden: !isMobile,
      leftIcon: { iconName: 'ClipboardPlus' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname === APP_ROUTE.PRIORITIES,
        onClick: () => navigate(APP_ROUTE.PRIORITIES),
      },
    },
    {
      id: 'sidemenu-library',
      title: SIDEBAR_TITLES.library,
      hidden: !isMobile,
      leftIcon: { iconName: 'ChartBreakoutCircle' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname === APP_ROUTE.LIBRARY,
        onClick: () => navigate(APP_ROUTE.LIBRARY),
      },
    },
    {
      id: 'sidemenu-life-readiness',
      title: SIDEBAR_TITLES.lifeReadiness,
      hidden: !isMobile,
      leftIcon: { iconName: 'Speedometer03' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname.startsWith(APP_ROUTE.LIFE_READINESS),
        onClick: () => navigate(APP_ROUTE.LIFE_READINESS),
      },
    },
    {
      id: 'sidemenu-bookmarks',
      title: SIDEBAR_TITLES.bookmarks,
      hidden: !isMobile,
      leftIcon: { iconName: 'BookmarkCheck' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: location.pathname === APP_ROUTE.BOOKMARKS,
        onClick: () => navigate(APP_ROUTE.BOOKMARKS),
      },
    },
    // MOBILE ONLY SIDEBAR ITEMS - END
    {
      id: 'sidemenu-networks',
      title: SIDEBAR_TITLES.networks,
      hidden: !userPermissions?.access_networks.allowed || IS_SANDBOX,
      leftIcon: { iconName: 'Dataflow01' },
      rightIcon: { iconName: 'Share04' },
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: false,
        onClick: () => window.open(`${BACKRS_APP_URL}/tabs/feed`, '_blank'),
      },
    },
    {
      id: 'sidemenu-more',
      title: SIDEBAR_TITLES.more,
      hidden: isLoggedOut || isMobile,
      leftIcon: { iconName: 'Menu01' },
      variant: {
        type: 'EXPANDABLE',
        subItems: [
          {
            id: 'sidemenu-more-blueprint',
            title: SIDEBAR_TITLES.blueprint,
            hidden: !isInstitutionAdmin,
            leftIcon: { iconName: 'Map01', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: isBlueprintActive,
              onClick: () => navigate(blueprintPath),
            },
          },
          {
            id: 'sidemenu-more-assess-my-track',
            title: SIDEBAR_TITLES.assessMyTrack,
            hidden:
              !isInstitutionAdmin ||
              (!!academicEnrollmentTypes && isUnsupportedEnrollmentType(academicEnrollmentTypes)),
            leftIcon: { iconName: 'Route', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname === APP_ROUTE.ASSESS_MY_TRACK,
              onClick: () => navigate(APP_ROUTE.ASSESS_MY_TRACK),
            },
          },
          {
            id: 'sidemenu-more-tools',
            title: SIDEBAR_TITLES.tools,
            hidden: !isInstitutionAdmin,
            leftIcon: { iconName: 'PuzzlePiece01', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname === APP_ROUTE.TOOLS,
              onClick: () => navigate(APP_ROUTE.TOOLS),
            },
          },
          {
            id: 'sidemenu-more-coaching',
            title: SIDEBAR_TITLES.coaching,
            hidden:
              !isInstitutionAdmin ||
              !isCoachingEnabled ||
              !userPermissions?.access_deep_dives.allowed,
            leftIcon: { iconName: 'StickerCircle', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname.startsWith(APP_ROUTE.DEEP_DIVES),
              onClick: () => navigate(APP_ROUTE.DEEP_DIVES),
            },
          },
          {
            id: 'sidemenu-more-priorities',
            title: SIDEBAR_TITLES.priorities,
            leftIcon: { iconName: 'ClipboardPlus', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname === APP_ROUTE.PRIORITIES,
              onClick: () => navigate(APP_ROUTE.PRIORITIES),
            },
          },
          {
            id: 'sidemenu-more-library',
            title: SIDEBAR_TITLES.library,
            leftIcon: { iconName: 'ChartBreakoutCircle', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname === APP_ROUTE.LIBRARY,
              onClick: () => navigate(APP_ROUTE.LIBRARY),
            },
          },
          {
            id: 'sidemenu-more-life-readiness',
            title: SIDEBAR_TITLES.lifeReadiness,
            leftIcon: { iconName: 'Speedometer03', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname.startsWith(APP_ROUTE.LIFE_READINESS),
              onClick: () => navigate(APP_ROUTE.LIFE_READINESS),
            },
          },
          {
            id: 'sidemenu-more-bookmarks',
            title: SIDEBAR_TITLES.bookmarks,
            leftIcon: { iconName: 'BookmarkCheck', diameter: '16px' },
            variant: {
              type: 'NAVIGATION_ITEM',
              itemVariant: 'secondary',
              isActive: location.pathname === APP_ROUTE.BOOKMARKS,
              onClick: () => navigate(APP_ROUTE.BOOKMARKS),
            },
          },
        ],
      },
    },
    ...(isInstitutionAdmin ? [] : [coachingDashboardMenuItem, coachingAnalyticsMenuItem]),
    {
      title: SIDEBAR_TITLES.superuser,
      hidden: !userPermissions?.access_super_user.allowed,
      leftIcon: { iconName: 'UserUp02' },
      variant: {
        isActive: location.pathname === APP_ROUTE.OFFICE_HOURS_SUPERUSER,
        type: 'NAVIGATION_ITEM',
        onClick: () => navigate(APP_ROUTE.OFFICE_HOURS_SUPERUSER),
      },
    },
    {
      id: 'sidemenu-getting-started',
      title: SIDEBAR_TITLES.gettingStarted,
      hidden: !isMobile,
      variant: {
        type: 'NAVIGATION_ITEM',
        isActive: false,
        onClick: onOpenGettingStarted,
      },
    },
    {
      id: 'sidemenu-operator-context',
      title: institutionLabel(
        heldRoots.find((root) => root.id === operatorInstitutionId) ?? {
          id: operatorInstitutionId,
          name: null,
        },
      ),
      hidden: heldRoots.length < 2 || operatorInstitutionId === null,
      leftIcon: { iconName: 'Building08' },
      variant: {
        type: 'EXPANDABLE',
        subItems: heldRoots.map((root) => ({
          id: `sidemenu-operator-context-${root.id}`,
          title: institutionLabel(root),
          leftIcon: { iconName: 'Building08', diameter: '16px' },
          variant: {
            type: 'NAVIGATION_ITEM' as const,
            itemVariant: 'secondary' as const,
            isActive: root.id === operatorInstitutionId,
            onClick: () => onOperatorInstitutionSelect(root.id),
          },
        })),
      },
    },
  ];
};
